import React,{useEffect,useMemo,useRef,useState} from 'react';
import {useNavigate,useParams} from 'react-router-dom';
import {useSupabaseData} from '../contexts/SupabaseDataContext';
import {useSupabaseAuth} from '../contexts/SupabaseAuthContext';
import {format,isToday,isYesterday,startOfDay} from 'date-fns';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {
  FiSend,
  FiMessageCircle,
  FiUser,
  FiLoader,
  FiSearch,
  FiArrowLeft,
  FiExternalLink
}=FiIcons;

const getLatestConversationOrder=(orders,sellers,userId,partnerId)=> {
  return orders
    .filter((order)=> {
      const sellerUserId=order.seller?.user_id ||
        sellers.find((seller)=> seller.id===order.seller_id)?.user_id;

      const isBuyerConversation=
        order.buyer_id===userId &&
        sellerUserId===partnerId;

      const isSellerConversation=
        sellerUserId===userId &&
        order.buyer_id===partnerId;

      return isBuyerConversation || isSellerConversation;
    })
    .sort((first,second)=> (
      new Date(second.created_at)-new Date(first.created_at)
    ))[0] || null;
};

const getConversationProfile=(messages,partnerId)=>{
  const matchingMessage=[...messages].reverse().find((message)=>(
    message.sender_id===partnerId
    || message.receiver_id===partnerId
  ));

  if (!matchingMessage) return null;

  return matchingMessage.sender_id===partnerId
    ? matchingMessage.sender_profile
    : matchingMessage.receiver_profile;
};

const ChatPage=()=> {
  const navigate=useNavigate();
  const {userId}=useParams();
  const {
    user,
    loading: authLoading,
    isInitialized
  }=useSupabaseAuth();
  const {
    messages,
    sellers,
    orders,
    isDataReady,
    sendMessage,
    markMessagesAsRead
  }=useSupabaseData();

  const [newMessage,setNewMessage]=useState('');
  const [error,setError]=useState(null);
  const [isSending,setIsSending]=useState(false);
  const [searchQuery,setSearchQuery]=useState('');
  const messagesEndRef=useRef(null);
  const markedConversationRef=useRef(null);

  const conversations=useMemo(()=> {
    if (!user || !messages) return {};

    return messages.reduce((groups,message)=> {
      const otherUserId=message.sender_id===user.id
        ? message.receiver_id
        : message.sender_id;

      if (!otherUserId) return groups;
      if (!groups[otherUserId]) groups[otherUserId]=[];
      groups[otherUserId].push(message);
      return groups;
    },{});
  },[messages,user]);

  const getPartnerSeller=(partnerId)=> sellers.find(
    (seller)=> seller.user_id===partnerId
  );

  const getConversationOrder=(partnerId)=> getLatestConversationOrder(
    orders || [],
    sellers || [],
    user?.id,
    partnerId
  );

  const getConversationName=(partnerId,conversationMessages=[])=> {
    const seller=getPartnerSeller(partnerId);
    if (seller?.business_name) return seller.business_name;

    const order=getConversationOrder(partnerId);
    if (order?.buyer?.name) return order.buyer.name;

    return getConversationProfile(conversationMessages,partnerId)?.name
      || 'User';
  };

  const sortedConversations=useMemo(()=> {
    return Object.entries(conversations)
      .map(([conversationUserId,conversationMessages])=> {
        const orderedMessages=[...conversationMessages].sort(
          (first,second)=> new Date(first.created_at)-new Date(second.created_at)
        );

        return {
          userId: conversationUserId,
          messages: orderedMessages,
          lastMessage: orderedMessages[orderedMessages.length-1],
          name: getConversationName(
            conversationUserId,
            orderedMessages
          )
        };
      })
      .filter((conversation)=> conversation.name.toLowerCase().includes(
        searchQuery.toLowerCase()
      ))
      .sort((first,second)=> (
        new Date(second.lastMessage.created_at)-
        new Date(first.lastMessage.created_at)
      ));
  },[conversations,searchQuery,sellers,orders,user?.id]);

  const activeConversation=useMemo(()=> (
    userId ? conversations[userId] || [] : []
  ),[userId,conversations]);

  const activeSeller=getPartnerSeller(userId);
  const activeOrder=getConversationOrder(userId);
  const activePartnerName=activeSeller?.business_name ||
    activeOrder?.buyer?.name ||
    getConversationProfile(activeConversation,userId)?.name ||
    'User';

  const groupedMessages=useMemo(()=> {
    const sorted=[...activeConversation].sort(
      (first,second)=> new Date(first.created_at)-new Date(second.created_at)
    );
    const groups=[];
    let currentGroup=null;

    sorted.forEach((message)=> {
      const date=new Date(message.created_at);
      const dateStr=format(startOfDay(date),'yyyy-MM-dd');

      if (!currentGroup || currentGroup.dateStr!==dateStr) {
        currentGroup={
          dateStr,
          displayDate: isToday(date)
            ? 'Today'
            : isYesterday(date)
              ? 'Yesterday'
              : format(date,'MMMM d,yyyy'),
          messages:[]
        };
        groups.push(currentGroup);
      }

      currentGroup.messages.push(message);
    });

    return groups;
  },[activeConversation]);

  const scrollToBottom=(behavior='smooth')=> {
    messagesEndRef.current?.scrollIntoView({
      behavior,
      block:'nearest'
    });
  };

  useEffect(()=> {
    if (!userId || !user?.id) {
      markedConversationRef.current=null;
      return;
    }

    const conversationKey=`${user.id}:${userId}`;
    if (markedConversationRef.current===conversationKey) return;

    markedConversationRef.current=conversationKey;
    markMessagesAsRead(userId).catch((markError)=> {
      console.error('Failed to mark messages as read:',markError);
      markedConversationRef.current=null;
    });

    window.setTimeout(()=> scrollToBottom('auto'),100);
  },[userId,user?.id]);

  useEffect(()=> {
    if (userId) scrollToBottom('smooth');
  },[userId,activeConversation.length]);

  const handleSendMessage=async (event)=> {
    event.preventDefault();
    if (!newMessage.trim() || !userId || isSending) return;

    setIsSending(true);
    const text=newMessage.trim();
    setNewMessage('');
    setError(null);

    try {
      await sendMessage({
        receiver_id:userId,
        body:text
      });
      window.setTimeout(()=> scrollToBottom('smooth'),100);
    } catch (sendError) {
      setError(sendError.message);
      setNewMessage(text);
    } finally {
      setIsSending(false);
    }
  };

  const openOrder=()=> {
    if (!activeOrder?.id) return;

    const isSelling=activeOrder.seller?.user_id===user?.id ||
      activeOrder.seller_id===sellers.find(
        (seller)=> seller.user_id===user?.id
      )?.id;

    navigate(`/orders?order=${activeOrder.id}&mode=${isSelling ? 'selling' : 'buying'}`);
  };

  const openSellerProfile=()=> {
    if (activeSeller?.id) navigate(`/seller/${activeSeller.id}`);
  };

  if (!isInitialized || authLoading) {
    return (
      <div className="h-full flex flex-col items-center justify-center bg-gray-50">
        <SafeIcon icon={FiLoader} className="w-10 h-10 text-emerald-600 animate-spin mb-4" />
        <p className="text-[10px] font-black text-emerald-600 uppercase tracking-widest">
          Waking up Chat...
        </p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="h-full flex flex-col items-center justify-center p-8 text-center bg-gray-50">
        <SafeIcon icon={FiMessageCircle} className="text-5xl text-emerald-200 mb-6" />
        <h2 className="text-2xl font-bold text-gray-900">Sign in to message</h2>
        <button
          onClick={()=> navigate('/login')}
          className="mt-8 px-10 py-4 bg-emerald-600 text-white rounded-2xl font-black"
        >
          Login
        </button>
      </div>
    );
  }

  return (
    <div className="h-full bg-gray-50 flex flex-col overflow-hidden">
      <div className="flex-1 flex w-full max-w-7xl mx-auto md:p-4 lg:p-6 gap-4 overflow-hidden">
        <div className={`${userId ? 'hidden md:flex' : 'flex'} w-full md:w-80 lg:w-96 bg-white md:rounded-2xl shadow-sm border border-gray-200 flex-col overflow-hidden`}>
          <div className="p-5 border-b border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-black text-gray-900">Messages</h2>
              <SafeIcon icon={FiMessageCircle} className="text-emerald-600" />
            </div>

            <div className="relative">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(event)=> setSearchQuery(event.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-gray-50 border-none rounded-xl text-sm font-medium"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {!isDataReady ? (
              <div className="p-8 text-center">
                <SafeIcon icon={FiLoader} className="animate-spin inline text-emerald-600" />
              </div>
            ) : sortedConversations.length>0 ? (
              sortedConversations.map((conversation)=> {
                const unreadInConversation=conversation.messages.filter(
                  (message)=> message.receiver_id===user.id && !message.read_at
                ).length;

                return (
                  <div
                    key={conversation.userId}
                    onClick={()=> navigate(`/chat/${conversation.userId}`)}
                    className={`p-4 border-b border-gray-50 cursor-pointer transition-all ${
                      userId===conversation.userId
                        ? 'bg-emerald-50'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 rounded-2xl bg-gray-100 flex items-center justify-center overflow-hidden">
                        <SafeIcon icon={FiUser} className="text-gray-400" />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-baseline">
                          <h3 className="text-sm font-black text-slate-900 truncate">
                            {conversation.name}
                          </h3>
                          <span className="text-[10px] text-gray-400 font-medium">
                            {format(new Date(conversation.lastMessage.created_at),'HH:mm')}
                          </span>
                        </div>

                        <p className="text-xs truncate text-gray-400 font-medium">
                          {conversation.lastMessage.body}
                        </p>
                      </div>

                      {unreadInConversation>0 && (
                        <span className="w-5 h-5 bg-emerald-600 text-white text-[8px] flex items-center justify-center rounded-full font-black">
                          {unreadInConversation}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="p-12 text-center">
                <p className="text-xs font-black text-slate-400 uppercase tracking-widest">
                  No conversations
                </p>
              </div>
            )}
          </div>
        </div>

        <div className={`${!userId ? 'hidden md:flex' : 'flex'} flex-1 bg-white md:rounded-2xl shadow-sm border border-gray-200 flex-col overflow-hidden`}>
          {userId ? (
            <>
              <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-white z-10 flex-shrink-0">
                <div className="flex items-center min-w-0">
                  <button
                    onClick={()=> navigate('/chat')}
                    className="md:hidden mr-3 p-2 hover:bg-slate-50 rounded-xl transition-all"
                  >
                    <SafeIcon icon={FiArrowLeft} className="text-slate-900" />
                  </button>

                  <div className="w-10 h-10 rounded-xl bg-emerald-600 flex items-center justify-center text-white overflow-hidden flex-shrink-0">
                    {activeSeller?.profile_image ? (
                      <img
                        src={activeSeller.profile_image}
                        className="w-full h-full object-cover"
                        alt=""
                      />
                    ) : (
                      <SafeIcon icon={FiUser} />
                    )}
                  </div>

                  <div className="ml-3 min-w-0">
                    {activeSeller ? (
                      <button
                        type="button"
                        onClick={openSellerProfile}
                        className="text-sm font-black text-gray-900 tracking-tight hover:text-emerald-600 transition-colors truncate text-left"
                      >
                        {activePartnerName}
                      </button>
                    ) : (
                      <h3 className="text-sm font-black text-gray-900 tracking-tight truncate">
                        {activePartnerName}
                      </h3>
                    )}

                    <div className="flex items-center">
                      <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full mr-2" />
                      <span className="text-[10px] font-black text-emerald-600 uppercase tracking-widest">
                        Active Partner
                      </span>
                    </div>
                  </div>
                </div>

                <div className="ml-4 flex-shrink-0 text-right">
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
                    Latest Order
                  </p>
                  {activeOrder ? (
                    <button
                      type="button"
                      onClick={openOrder}
                      className="inline-flex items-center gap-1 text-xs font-black text-indigo-600 hover:text-indigo-800 transition-colors"
                    >
                      #{activeOrder.id.slice(0,8).toUpperCase()}
                      <SafeIcon icon={FiExternalLink} className="text-[10px]" />
                    </button>
                  ) : (
                    <span className="text-xs font-bold text-slate-300">
                      No order
                    </span>
                  )}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-6 bg-gray-50/30 flex flex-col">
                <div className="flex-1" />

                {groupedMessages.map((group)=> (
                  <div key={group.dateStr} className="space-y-6">
                    <div className="flex justify-center">
                      <span className="px-4 py-1 bg-white border border-slate-100 text-slate-400 text-[9px] font-black uppercase tracking-widest rounded-full shadow-sm">
                        {group.displayDate}
                      </span>
                    </div>

                    {group.messages.map((message)=> (
                      <div
                        key={message.id}
                        className={`flex ${
                          message.sender_id===user.id
                            ? 'justify-end'
                            : 'justify-start'
                        }`}
                      >
                        <div className={`max-w-[85%] px-4 py-3 rounded-2xl shadow-sm ${
                          message.sender_id===user.id
                            ? 'bg-emerald-600 text-white rounded-tr-none'
                            : 'bg-white text-gray-800 rounded-tl-none border border-gray-100'
                        }`}>
                          <p className="text-sm font-medium leading-relaxed">
                            {message.body}
                          </p>
                          <div className={`text-[9px] mt-1.5 ${
                            message.sender_id===user.id
                              ? 'text-emerald-100'
                              : 'text-gray-400'
                          } text-right font-black`}>
                            {format(new Date(message.created_at),'HH:mm')}
                            {message.sender_id===user.id && message.read_at && (
                              <span className="ml-1 opacity-60">✓✓</span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ))}

                <div ref={messagesEndRef} />
              </div>

              <div className="p-4 border-t border-gray-100 bg-white">
                {error && (
                  <p className="mb-3 text-xs font-bold text-red-600">{error}</p>
                )}

                <form
                  onSubmit={handleSendMessage}
                  className="flex items-center space-x-3"
                >
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(event)=> setNewMessage(event.target.value)}
                    placeholder="Type a message..."
                    className="flex-1 bg-gray-50 border-none rounded-2xl px-5 py-3.5 text-sm font-medium focus:ring-2 focus:ring-emerald-500 focus:bg-white transition-all shadow-inner"
                  />
                  <button
                    type="submit"
                    disabled={!newMessage.trim() || isSending}
                    className="h-12 w-12 bg-emerald-600 text-white rounded-2xl flex items-center justify-center disabled:opacity-50 shadow-lg shadow-emerald-100 active:scale-95 transition-all"
                  >
                    <SafeIcon icon={FiSend} className="text-lg" />
                  </button>
                </form>
              </div>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-center p-12">
              <div className="w-24 h-24 bg-emerald-50 rounded-[2rem] flex items-center justify-center mb-6 shadow-inner">
                <SafeIcon icon={FiMessageCircle} className="text-4xl text-emerald-200" />
              </div>
              <h3 className="text-xl font-black text-slate-900 tracking-tight mb-2">
                Secure Messenger
              </h3>
              <p className="text-xs font-bold text-gray-400 uppercase tracking-widest max-w-[200px]">
                Select a conversation to start chatting
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatPage;
